import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Input, Spinner, Table } from "reactstrap";
import NoteReviewReader from "./NoteReviewReader";
import "./NoteAccuracyPanel.css";

export const ACCURACY_TAGS = {
  scribe_missed: "Scribe missed information",
  scribe_hallucinated: "Scribe added unsupported information",
  clinician_supplied: "Clinician supplied new information",
  clinician_removed: "Clinician removed supported information",
  cosmetic: "Cosmetic changes",
  style_and_tone: "Style and tone",
  conciseness: "Made more concise",
};

// Mirrors Notes.MULTI_RECORD_TYPE_SQL. classic_or_plus: captured before the
// feature flags were stored (2026-09-25), so classic and ++ cannot be told apart.
export const MULTI_RECORD_LABELS = {
  classic: "Classic multi-record",
  plus: "MultiRecord++",
  segmented: "Segmented (extra context)",
  classic_or_plus: "Classic or ++ (before flags were recorded)",
  ambient: "Ambient multi-visit",
  mixed: "Mixed multi-record",
};

export const NoteTypeCell = ({ note }) => <>
  {note.noteType || "Unknown"}
  {note.multiRecordType && <span className="note-accuracy-multi-record">{MULTI_RECORD_LABELS[note.multiRecordType] || note.multiRecordType}</span>}
</>;

export const ACCURACY_TAG_DEFINITIONS = {
  scribe_missed: "The clinician added something that was in the visit transcript or typed visit notes, but Comprehend left it out of the note.",
  scribe_hallucinated: "Comprehend wrote something that was not in the transcript, or that the transcript contradicts, and the clinician removed or corrected it.",
  clinician_supplied: "The clinician added something that was not in the transcript, such as their own knowledge of the visit. Comprehend could not have known it.",
  clinician_removed: "Comprehend correctly captured something from the transcript and the clinician removed it anyway. This is a preference, not a mistake by Comprehend.",
  cosmetic: "How the note is presented changed, such as bullets versus prose, structure, ordering, headings, spelling, punctuation, or capitalization.",
  style_and_tone: "The clinician reworded it: voice, tense, person, terminology, or abbreviations.",
  conciseness: "The clinician said the same thing in less text.",
};

const number = (value) => Number(value || 0).toLocaleString();
const percent = (value) => value == null ? "—" : `${Number(value).toFixed(1)}%`;
const dateLabel = (value) => value ? new Date(value).toLocaleDateString() : "—";

export const TagLabels = ({ tags = [], selectedTag }) => (
  <div className="note-accuracy-tags">
    {tags.map((tag) => <span key={tag} className={`note-accuracy-tag${tag === selectedTag ? " note-accuracy-tag-selected" : ""}`}>
      {ACCURACY_TAGS[tag] || tag}
    </span>)}
  </div>
);

export default function NoteAccuracyPanel({ apiClient, filters }) {
  const [scope, setScope] = useState({});
  const [tag, setTag] = useState("");
  const [view, setView] = useState("reasons");
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState("");
  const [notes, setNotes] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [pages, setPages] = useState([]);
  const [sort, setSort] = useState(filters.userIds?.length === 1 ? "recent" : "clinicians");
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesError, setNotesError] = useState("");
  const [retry, setRetry] = useState(0);
  const [selected, setSelected] = useState(null);
  const request = useMemo(() => ({
    ...filters, userIds: scope.userId ? [scope.userId] : filters.userIds, status: "finalized",
  }), [filters, scope]);

  useEffect(() => {
    let active = true;
    setSummaryLoading(true);
    setSummaryError("");
    apiClient.JMPost("/v1/notes/accuracy-tags", request).then((response) => {
      if (!active) return;
      if (response?.error) throw new Error(response.error);
      setSummary(response);
    }).catch(() => {
      if (active) setSummaryError("Unable to load accuracy tags. Try again.");
    }).finally(() => { if (active) setSummaryLoading(false); });
    return () => { active = false; };
  }, [apiClient, request, retry]);

  useEffect(() => {
    if (view !== "notes") return undefined;
    let active = true;
    setNotesLoading(true);
    setNotesError("");
    apiClient.JMPost("/v1/notes/accuracy-tags/notes", { ...request, tag: tag || undefined, sort, cursor }).then((response) => {
      if (!active) return;
      if (response?.error) throw new Error(response.error);
      setNotes(response?.notes || []);
      setNextCursor(response?.nextCursor || null);
    }).catch(() => {
      if (active) setNotesError("Unable to load notes. Try again.");
    }).finally(() => { if (active) setNotesLoading(false); });
    return () => { active = false; };
  }, [apiClient, request, tag, view, sort, cursor, retry]);

  const resetPages = () => { setCursor(null); setNextCursor(null); setPages([]); setSelected(null); };
  const selectTag = (nextTag) => { setTag(nextTag); resetPages(); };

  return (
    <section className="note-intel-panel note-accuracy-panel" aria-label="Accuracy tags and note explorer">
      <div className="note-accuracy-heading">
        <div><h3>Why notes change</h3><p>Select an edit reason to inspect matching notes and their source.</p></div>
      </div>
      <details className="note-accuracy-guide">
        <summary>What do these labels mean?</summary>
        <p>
          After a clinician signs a note, an AI model compares Comprehend's draft with the
          signed note and the visit transcript, and labels why each section changed. This
          shows whether edits come from Comprehend missing or inventing information, or from
          clinician preferences, which tells us what to fix.
        </p>
        <dl>
          {Object.entries(ACCURACY_TAGS).map(([key, label]) => <div key={key}>
            <dt>{label}</dt><dd>{ACCURACY_TAG_DEFINITIONS[key]}</dd>
          </div>)}
        </dl>
        <p>
          <strong>Share of tagged notes</strong> is the percentage of labeled notes that carry
          that label. Only signed notes with more than {summary?.threshold ?? 3}% of their text
          changed are labeled, so unedited and barely edited notes are not in this share. A
          note can have several labels, so the shares add up to more than 100%.
          <strong> Clinicians</strong> is how many different clinicians have at least one
          note with the label.
        </p>
      </details>
      <div className="note-accuracy-toolbar">
        <div className="note-accuracy-tabs" role="group" aria-label="Accuracy views">
          <button type="button" aria-pressed={view === "reasons"} onClick={() => setView("reasons")}>Edit reasons</button>
          <button type="button" aria-pressed={view === "notes"} onClick={() => setView("notes")}>Explore notes</button>
        </div>
        {scope.userId && <Button size="sm" outline onClick={() => { setScope({}); resetPages(); }}>
          {scope.userId} <span aria-hidden="true">×</span><span className="sr-only">Clear filter</span>
        </Button>}
      </div>
      {view === "reasons" && <div aria-busy={summaryLoading}>
        {summaryError && <Alert color="danger">{summaryError} <Button size="sm" onClick={() => setRetry(retry + 1)}>Retry</Button></Alert>}
        {summaryLoading ? <div className="note-accuracy-loading"><Spinner size="sm" /> Loading accuracy labels…</div> : !summaryError && <>
          <div className="table-responsive"><Table className="note-accuracy-table" hover>
            <thead><tr><th>Edit reason</th><th>Share of tagged notes</th><th>Clinicians</th></tr></thead>
            <tbody>{Object.entries(ACCURACY_TAGS).map(([key, label]) => {
              const row = summary?.tags?.find((item) => item.tag === key) || {};
              return <tr key={key}>
                <td><button className="note-accuracy-link" disabled={!row.notes} onClick={() => { selectTag(key); setView("notes"); }} aria-label={`Review notes: ${label}`}>{label} <span aria-hidden="true">→</span></button></td>
                <td><span className="note-accuracy-rate"><span className="note-accuracy-rate-track" aria-hidden="true"><span style={{ width: `${row.rate || 0}%` }} /></span>{percent(row.rate)}</span></td><td>{number(row.clinicians)}</td>
              </tr>;
            })}</tbody>
          </Table></div>
        </>}
      </div>}
      {view === "notes" && <div aria-busy={notesLoading}>
        <div className="note-accuracy-toolbar">
          <label>Edit reason<Input type="select" value={tag} onChange={(event) => selectTag(event.target.value)}>
            <option value="">All signed, compared notes</option>{Object.entries(ACCURACY_TAGS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </Input></label>
          <label>Order notes by<Input type="select" value={sort} onChange={(event) => { setSort(event.target.value); resetPages(); }}><option value="clinicians">Across clinicians</option><option value="recent">Most recent</option><option value="mostEdited">Largest text change</option></Input></label>
        </div>
        {sort === "clinicians" && <p className="note-accuracy-caption">One recent matching note per clinician. These examples are not a random sample.</p>}
        {notesError && <Alert color="danger">{notesError} <Button size="sm" onClick={() => setRetry(retry + 1)}>Retry</Button></Alert>}
        {notesLoading ? <div className="note-accuracy-loading"><Spinner size="sm" /> Loading notes…</div> : !notesError && <>
          <div className="note-accuracy-notes-scroll" role="region" aria-label="Captured notes" tabIndex={0}>
            <Table className="note-accuracy-table note-accuracy-notes-table" hover>
              <thead><tr><th>Signed / note type</th><th>Clinician / EMR</th><th>Accuracy labels</th><th>Text change</th><th><span className="sr-only">Review</span></th></tr></thead>
              <tbody>{notes.map((note, index) => <tr key={note.noteId}>
                <td data-label="Signed / note type">{dateLabel(note.signedAt)}<small><NoteTypeCell note={note} /></small></td>
                <td data-label="Clinician / EMR"><button className="note-accuracy-link" onClick={() => { setScope((current) => ({ ...current, userId: note.userId })); setSort("recent"); resetPages(); }} aria-label={`Browse notes for ${note.userId}`}>{note.userId}</button><small>{note.emr || "Unknown"}</small></td>
                <td data-label="Accuracy labels">{note.tags.length ? <TagLabels tags={note.tags} selectedTag={tag} /> : <span className="note-accuracy-muted">Not tagged</span>}</td>
                <td data-label="Text change">{percent(note.pctChanged)}</td><td><Button size="sm" outline onClick={() => setSelected(index)} aria-label={`Review ${note.noteType || "note"} for ${note.userId} signed ${dateLabel(note.signedAt)}`}>Review</Button></td>
              </tr>)}</tbody>
            </Table>
          </div>
          {!notes.length && <p className="note-accuracy-empty">No notes match these filters.</p>}
          <div className="note-accuracy-pagination">
            <Button size="sm" outline disabled={!pages.length || notesLoading} onClick={() => { setCursor(pages[pages.length - 1]); setPages(pages.slice(0, -1)); }}>Previous page</Button>
            <span>Page {pages.length + 1} · {notes.length} notes</span>
            <Button size="sm" outline disabled={!nextCursor || notesLoading} onClick={() => { setPages([...pages, cursor]); setCursor(nextCursor); }}>Next page</Button>
          </div>
        </>}
      </div>}
      {selected !== null && notes[selected] && <NoteReviewReader
        apiClient={apiClient} note={notes[selected]} index={selected} count={notes.length}
        preferredTag={tag}
        tagLabels={ACCURACY_TAGS}
        onClose={() => setSelected(null)} onNavigate={setSelected}
      />}
    </section>
  );
}
